import _slicedToArray from "C:\\Users\\Kristaps\\projects\\proj261\\site\\node_modules\\@babel\\runtime\\helpers\\esm\\slicedToArray.js";
var _excluded = ["id", "autoFocus", "bordered", "views", "tabIndex", "disabled", "readOnly", "className", "value", "defaultValue", "onChange", "currentDate", "defaultCurrentDate", "onCurrentDateChange", "min", "max", "view", "defaultView", "onViewChange", "onKeyDown", "onNavigate", "renderDay", "messages", "formats"];

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

import cn from 'classnames';
import React, { useEffect, useRef } from 'react';
import { useUncontrolledProp } from 'uncontrollable';
import CalendarHeader from './CalendarHeader';
import Century from './Century';
import Decade from './Decade';
import { useLocalizer } from './Localization';
import Month from './Month';
import SlideTransitionGroup from './SlideTransitionGroup';
import Widget from './Widget';
import Year from './Year';
import dates from './dates';
import useAutoFocus from './useAutoFocus';
import useFocusManager from './useFocusManager';
import { notify, useInstanceId } from './WidgetHelpers';

var last = function last(a) {
  return a[a.length - 1];
};

var CELL_CLASSNAME = 'rw-cell';
var FOCUSED_CELL_SELECTOR = ".".concat(CELL_CLASSNAME, "[tabindex]");
var MIN = new Date(1900, 0, 1);
var MAX = new Date(2099, 11, 31);
var VIEW_OPTIONS = ['month', 'year', 'decade', 'century'];
var VIEW_UNIT = {
  month: 'day',
  year: 'month',
  decade: 'year',
  century: 'decade'
};
var VIEW = {
  month: Month,
  year: Year,
  decade: Decade,
  century: Century
};
var ARROWS_TO_DIRECTION = {
  ArrowDown: 'DOWN',
  ArrowUp: 'UP',
  ArrowRight: 'RIGHT',
  ArrowLeft: 'LEFT'
};
var OPPOSITE_DIRECTION = {
  LEFT: 'RIGHT',
  RIGHT: 'LEFT'
};
var MULTIPLIER = {
  year: 1,
  decade: 10,
  century: 100
};

function inRangeValue(_value, min, max) {
  var value = dateOrNull(_value);
  if (value === null) return value;
  return dates.max(dates.min(value, max), min);
}

var useViewState = function useViewState(views) {
  var view = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : views[0];
  var currentDate = arguments.length > 2 ? arguments[2] : undefined;
  var lastView = useRef(view);
  var lastDate = useRef(currentDate);
  var slideDirection;

  if (view !== lastView.current) {
    slideDirection = views.indexOf(lastView.current) > views.indexOf(view) ? 'top' : 'bottom';
  } else if (lastDate.current !== currentDate) {
    slideDirection = dates.gt(currentDate, lastDate.current) ? 'left' : 'right';
  }

  useEffect(function () {
    lastDate.current = currentDate;
    lastView.current = view;
  });
  return slideDirection;
};
/**
 * @public
 */


function Calendar(_ref) {
  var id = _ref.id,
      autoFocus = _ref.autoFocus,
      _ref$bordered = _ref.bordered,
      bordered = _ref$bordered === void 0 ? true : _ref$bordered,
      _ref$views = _ref.views,
      views = _ref$views === void 0 ? VIEW_OPTIONS : _ref$views,
      _ref$tabIndex = _ref.tabIndex,
      tabIndex = _ref$tabIndex === void 0 ? 0 : _ref$tabIndex,
      disabled = _ref.disabled,
      readOnly = _ref.readOnly,
      className = _ref.className,
      value = _ref.value,
      defaultValue = _ref.defaultValue,
      onChange = _ref.onChange,
      pCurrentDate = _ref.currentDate,
      defaultCurrentDate = _ref.defaultCurrentDate,
      onCurrentDateChange = _ref.onCurrentDateChange,
      _ref$min = _ref.min,
      min = _ref$min === void 0 ? MIN : _ref$min,
      _ref$max = _ref.max,
      max = _ref$max === void 0 ? MAX : _ref$max,
      view = _ref.view,
      _ref$defaultView = _ref.defaultView,
      defaultView = _ref$defaultView === void 0 ? views[0] : _ref$defaultView,
      onViewChange = _ref.onViewChange,
      onKeyDown = _ref.onKeyDown,
      onNavigate = _ref.onNavigate,
      renderDay = _ref.renderDay,
      messages = _ref.messages,
      formats = _ref.formats,
      elementProps = _objectWithoutPropertiesLoose(_ref, _excluded);

  var _useUncontrolledProp = useUncontrolledProp(value, defaultValue, onChange),
      _useUncontrolledProp2 = _slicedToArray(_useUncontrolledProp, 2),
      currentValue = _useUncontrolledProp2[0],
      handleChange = _useUncontrolledProp2[1];

  var _useUncontrolledProp3 = useUncontrolledProp(pCurrentDate, defaultCurrentDate || currentValue || new Date(), onCurrentDateChange),
      _useUncontrolledProp4 = _slicedToArray(_useUncontrolledProp3, 2),
      currentDate = _useUncontrolledProp4[0],
      handleCurrentDateChange = _useUncontrolledProp4[1];

  var _useUncontrolledProp5 = useUncontrolledProp(view, defaultView, onViewChange),
      _useUncontrolledProp6 = _slicedToArray(_useUncontrolledProp5, 2),
      currentView = _useUncontrolledProp6[0],
      handleViewChange = _useUncontrolledProp6[1];

  var localizer = useLocalizer(messages, formats);
  var ref = useRef(null);
  var viewId = useInstanceId(id, '_calendar');
  var labelId = useInstanceId(id, '_calendar_label');
  useAutoFocus(!!autoFocus, ref);
  var slideDirection = useViewState(views, currentView, currentDate);

  var _useFocusManager = useFocusManager(ref, {
    disabled: disabled
  }, {
    willHandle: function willHandle() {
      if (tabIndex == -1) return false;
    }
  }),
      _useFocusManager2 = _slicedToArray(_useFocusManager, 2),
      focused = _useFocusManager2[1];

  var lastValue = useRef(currentValue);
  useEffect(function () {
    var inValue = inRangeValue(currentValue, min, max);
    var last = lastValue.current;
    lastValue.current = currentValue;
    if (!dates.eq(inValue, dateOrNull(last), VIEW_UNIT[currentView])) maybeSetCurrentDate(inValue);
  });
  var isDisabled = disabled || readOnly;
  /**
   * Handlers
   */

  var handleViewChangeImpl = function handleViewChangeImpl() {
    navigate('UP');
  };

  var handleMoveBack = function handleMoveBack() {
    navigate('LEFT');
  };

  var handleMoveForward = function handleMoveForward() {
    navigate('RIGHT');
  };

  var handleDateChange = function handleDateChange(date) {
    if (views[0] === currentView) {
      maybeSetCurrentDate(date);
      notify(handleChange, [date]);
      focus();
      return;
    }

    navigate('DOWN', date);
  };

  var handleMoveToday = function handleMoveToday() {
    var date = new Date();
    var firstView = views[0];
    notify(onChange, [date]);

    if (dates.inRange(date, min, max, firstView)) {
      focus();
      maybeSetCurrentDate(date);
      notify(handleViewChange, [firstView]);
    }
  };

  var handleKeyDown = function handleKeyDown(e) {
    var ctrl = e.ctrlKey || e.metaKey;
    var key = e.key;
    var direction = ARROWS_TO_DIRECTION[key];
    var unit = VIEW_UNIT[currentView];

    if (key === 'Enter') {
      e.preventDefault();
      return handleDateChange(currentDate);
    }

    if (direction) {
      if (ctrl) {
        e.preventDefault();
        navigate(direction);
      } else {
        var isRTL = getComputedStyle(e.currentTarget).getPropertyValue('direction') === 'rtl';
        if (isRTL && direction in OPPOSITE_DIRECTION) direction = OPPOSITE_DIRECTION[direction];

        var _nextDate = Calendar.move(currentDate, min, max, currentView, direction);

        if (!dates.eq(currentDate, _nextDate, unit)) {
          e.preventDefault();
          if (dates.gt(_nextDate, currentDate, currentView)) navigate('RIGHT', _nextDate);else if (dates.lt(_nextDate, currentDate, currentView)) navigate('LEFT', _nextDate);else maybeSetCurrentDate(_nextDate);
        }
      }
    }

    notify(onKeyDown, [e]);
  };

  function navigate(direction, date) {
    var nextView = currentView;
    var slideDir = direction === 'LEFT' || direction === 'UP' ? 'right' : 'left';
    if (direction === 'UP') nextView = views[views.indexOf(currentView) + 1] || nextView;
    if (direction === 'DOWN') nextView = views[views.indexOf(currentView) - 1] || nextView;
    if (!date) date = ['LEFT', 'RIGHT'].indexOf(direction) !== -1 ? nextDate(direction) : currentDate;

    if (dates.inRange(date, min, max, nextView)) {
      notify(onNavigate, [date, slideDir, nextView]); //this.focus()

      maybeSetCurrentDate(date);
      notify(handleViewChange, [nextView]);
    }
  }

  var focus = function focus() {
    var _ref$current;

    var node = (_ref$current = ref.current) == null ? void 0 : _ref$current.querySelector(FOCUSED_CELL_SELECTOR);
    node == null ? void 0 : node.focus();
  };

  var moveFocus = function moveFocus(node, hadFocus) {
    var current = document.activeElement;

    if (hadFocus && (!current || !node.contains(current))) {
      node.focus();
    }
  };

  function maybeSetCurrentDate(date) {
    var inRangeDate = inRangeValue(date ? new Date(date) : currentDate, min, max);
    if (date === currentDate || dates.eq(inRangeDate, dateOrNull(currentDate), VIEW_UNIT[currentView])) return;
    notify(handleCurrentDateChange, [inRangeDate]);
  }

  function nextDate(direction) {
    var method = direction === 'LEFT' ? 'subtract' : 'add';
    var unit = currentView === 'month' ? currentView : 'year';
    var multi = MULTIPLIER[currentView] || 1;
    return dates[method](currentDate, 1 * multi, unit);
  }

  function getHeaderLabel() {
    switch (currentView) {
      case 'month':
        return localizer.formatDate(currentDate, 'header');

      case 'year':
        return localizer.formatDate(currentDate, 'year');

      case 'decade':
        return localizer.formatDate(dates.startOf(currentDate, 'decade'), 'decade');

      case 'century':
        return localizer.formatDate(dates.startOf(currentDate, 'century'), 'century');
    }
  }

  var View = VIEW[currentView];
  var todayNotInRange = !dates.inRange(new Date(), min, max, currentView);
  var key = currentView + '_' + dates[currentView](currentDate); // let elementProps = Props.pickElementProps(this),
  // let viewProps = pick(uncontrolledProps, View)

  var prevDisabled = isDisabled || !dates.inRange(nextDate('LEFT'), min, max, currentView);
  var nextDisabled = isDisabled || !dates.inRange(nextDate('RIGHT'), min, max, currentView);
  return /*#__PURE__*/React.createElement(Widget, _extends({}, elementProps, {
    role: "group",
    ref: ref,
    focused: focused,
    disabled: disabled,
    readOnly: readOnly,
    tabIndex: tabIndex,
    className: cn(className, 'rw-calendar', bordered && 'rw-calendar-contained')
  }), /*#__PURE__*/React.createElement(CalendarHeader, {
    label: getHeaderLabel(),
    labelId: labelId,
    localizer: localizer,
    upDisabled: isDisabled || currentView === last(views),
    prevDisabled: prevDisabled,
    todayDisabled: isDisabled || todayNotInRange,
    nextDisabled: nextDisabled,
    onViewChange: handleViewChangeImpl,
    onMoveLeft: handleMoveBack,
    onMoveRight: handleMoveForward,
    onMoveToday: handleMoveToday
  }), /*#__PURE__*/React.createElement(Calendar.Transition, {
    direction: slideDirection,
    onTransitionEnd: moveFocus
  }, /*#__PURE__*/React.createElement(View, {
    key: key,
    min: min,
    max: max,
    id: viewId,
    value: currentValue,
    localizer: localizer,
    disabled: isDisabled,
    focusedItem: currentDate,
    onChange: handleDateChange,
    onKeyDown: handleKeyDown,
    "aria-labelledby": labelId,
    renderDay: renderDay
  })));
}

function dateOrNull(dt) {
  if (dt && !isNaN(dt.getTime())) return dt;
  return null;
}

Calendar.displayName = 'Calendar';
// Calendar.defaultProps = {
//   min: new Date(1900, 0, 1),
//   max: new Date(2099, 11, 31),
//   views: VIEW_OPTIONS,
//   tabIndex: '0',
// }
Calendar.Transition = SlideTransitionGroup;

Calendar.move = function (date, min, max, view, direction) {
  var isMonth = view === 'month';
  var isUpOrDown = direction === 'UP' || direction === 'DOWN';
  var rangeUnit = view && VIEW_UNIT[view];
  var addUnit = isMonth && isUpOrDown ? 'week' : VIEW_UNIT[view];
  var amount = isMonth || !isUpOrDown ? 1 : 4;
  var newDate;
  if (direction === 'UP' || direction === 'LEFT') amount *= -1;
  newDate = dates.add(date, amount, addUnit);
  return dates.inRange(newDate, min, max, rangeUnit) ? newDate : date;
};

export default Calendar;